<template>
  <BContainer
    v-if="currentUser"
    class="mb-4"
  >
    <div class="d-flex justify-content-end">
      <BBreadcrumb
        v-if="$route.meta.breadcrumb"
        :items="$route.meta.breadcrumb || []"
      />
    </div>

    <BRow
      class="mt-3 border-bottom"
    >
      <BCol
        cols="12"
        md="6"
        offset-md="6"
      >
        <ValidationObserver
          ref="formSynchronizer"
        >
          <div class="d-flex flex-column flex-md-row gap-2 justify-content-end">

            <ValidationProvider
              v-slot="{ errors }"
              vid="start_date"
              name="start date"
              rules="required"
            >
              <BFormGroup
                label="Start Date"
                label-for="synchronizeStartDate"
                label-class="font-weight-bolder"
                class="mb-2"
              >
                <BFormInput
                  id="synchronizeStartDate"
                  v-model="synchronize.start_date"
                  :state="(errors.length > 0 ? false : null)"
                  :disabled="state.synchronizing"
                  :max="synchronize.end_date"
                  debounce="1000"
                  type="date"
                />
                <div
                  v-if="(errors.length > 0)"
                  class="invalid-feedback"
                  v-text="errors[0]"
                />
              </BFormGroup>
            </ValidationProvider>

            <ValidationProvider
              v-slot="{ errors }"
              vid="end_date"
              name="end date"
              rules="required"
            >
              <BFormGroup
                label="End Date"
                label-for="synchronizeEndDate"
                label-class="font-weight-bolder"
                class="mb-2"
              >
                <BFormInput
                  id="synchronizeEndDate"
                  v-model="synchronize.end_date"
                  :state="(errors.length > 0 ? false : null)"
                  :disabled="state.synchronizing"
                  :min="synchronize.start_date"
                  debounce="1000"
                  type="date"
                />
                <div
                  v-if="(errors.length > 0)"
                  class="invalid-feedback"
                  v-text="errors[0]"
                />
              </BFormGroup>
            </ValidationProvider>

            <div class="d-flex flex-column">
              <BFormGroup
                class="mb-2"
              >
                <label
                  for="actionButtons"
                  class="d-none d-md-block"
                >
                  &nbsp;
                </label>
                <div class="d-flex gap-2">
                  <BOverlay
                    :show="state.synchronizing"
                    rounded="sm"
                    opacity="0.6"
                    spinner-small
                    spinner-variant="primary"
                  >
                    <BButton
                      :variant="`${state.synchronizing ? 'outline-dark' : 'primary'}`"
                      :disabled="(state.importing || state.synchronizing)"
                      block
                      type="button"
                      @click.prevent="synchronizeProducts"
                    >
                      Sync
                    </BButton>
                  </BOverlay>
                  <BOverlay
                    :show="state.importing"
                    rounded="sm"
                    opacity="0.6"
                    spinner-small
                    spinner-variant="secondary"
                  >
                    <BButton
                      :variant="`${state.importing ? 'outline-dark' : 'secondary'}`"
                      :disabled="(state.importing || state.synchronizing)"
                      block
                      type="button"
                      @click.prevent="importProducts"
                    >
                      Import
                    </BButton>
                  </BOverlay>
                </div>
              </BFormGroup>
            </div>
          </div>
        </ValidationObserver>
      </BCol>
    </BRow>

    <BRow
      class="mt-3"
    >
      <BCol
        cols="12"
        md="9"
      >
        <BFormGroup
          label="Search"
          label-for="filterProductSearch"
          label-class="font-weight-bolder"
        >
          <BFormInput
            id="filterProductSearch"
            v-model="tableProducts.filter.search"
            placeholder="search here"
            autocomplete="off"
            debounce="1000"
            type="text"
          />
        </BFormGroup>
      </BCol>

      <BCol
        cols="12"
        md="3"
      >
        <BFormGroup
          label="Category"
          label-for="filterCategory"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <VSelect
            v-model="tableProducts.selected.productCategory"
            input-id="filterCategory"
            placeholder="search category here"
            label="category_name"
            clearable
            append-to-body
            :options="(tableProducts.options.productCategories)"
            :loading="(tableProducts.fetching.productCategories)"
            :disabled="(tableProducts.busy || tableProducts.fetching.productCategories)"
            :calculate-position="calculateDropPosition"
            @search="searchFilterProductCategories"
          >
            <template #option="{ category_code, category_name }">
              <div class="d-flex flex-column flex-md-row py-1">
                <div class="flex-grow-1">
                  {{ category_name }}
                </div>
                <strong class="min-w-50px">{{ category_code }}</strong>
              </div>
            </template>
            <template #no-options="">
              no available categories
            </template>
          </VSelect>
        </BFormGroup>
      </BCol>
    </BRow>

    <CustomTable
      ref="tableProducts"
      :provider="tableProvider"
      :fields="tableProducts.fields"
      :filter="tableProducts.filter"
      :filter-on="tableProducts.filterOn"
      :sort-by="tableProducts.sortBy"
      :sort-desc="tableProducts.sortDesc"
      :sort-direction="tableProducts.sortDirection"
      :total-rows="tableProducts.totalRows"
      :current-page="tableProducts.currentPage"
      :per-page="tableProducts.perPage"
      :stacked="tableProducts.stacked"
      :busy="tableProducts.busy"
    >
      <template #cell(action)="{ item }">
        <div class="text-md-nowrap d-flex flex-column flex-md-row justify-content-center gap-2">
          <BButton
            size="sm"
            variant="primary"
            :disabled="tableProducts.busy"
            @click.prevent="editProduct(item)"
          >
            Edit
          </BButton>
        </div>
      </template>
    </CustomTable>

    <BModal
      ref="modalProduct"
      scrollable
      centered
      no-close-on-esc
      no-enforce-focus
      no-close-on-backdrop
      aria-hidden="false"
      role="dialog"
      title="Edit Product"
    >
      <ValidationObserver
        ref="formProduct"
      >
        <form
          role="form"
          novalidate
          @submit.prevent
        >
          <BRow>

            <BCol
              cols="12"
            >
              <BFormGroup
                label="Code"
                label-for="fProductCode"
                label-class="font-weight-bolder"
                class="mb-2"
              >
                <BFormInput
                  id="fProductCode"
                  v-model="formProduct.product_code"
                  placeholder="no product code"
                  disabled
                />
              </BFormGroup>
            </BCol>

            <BCol
              cols="12"
            >
              <BFormGroup
                label="Name"
                label-for="fProductName"
                label-class="font-weight-bolder"
                class="mb-2"
              >
                <BFormInput
                  id="fProductName"
                  v-model="formProduct.product_name"
                  placeholder="no product name"
                  disabled
                />
              </BFormGroup>
            </BCol>

            <BCol
              cols="12"
            >
              <ValidationProvider
                v-slot="{ errors }"
                vid="category"
                name="category"
                rules="required"
              >
                <BFormGroup
                  label="Category"
                  label-for="fProductCategory"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <VSelect
                    v-model="selected.productCategory"
                    input-id="fProductCategory"
                    placeholder="search category here"
                    label="category_name"
                    append-to-body
                    clearable
                    :options="(options.productCategories)"
                    :loading="(fetching.productCategories)"
                    :disabled="(state.busy || fetching.productCategories)"
                    :calculate-position="calculateDropPosition"
                    :class="{ 'is-invalid': errors.length > 0 }"
                    @search="searchProductCategories"
                  >
                    <template #option="{ category_code, category_name }">
                      <div class="d-flex flex-column flex-md-row py-1">
                        <div class="flex-grow-1">
                          {{ category_name }}
                        </div>
                        <strong class="min-w-50px">{{ category_code }}</strong>
                      </div>
                    </template>
                    <template #no-options="">
                      no available category
                    </template>
                  </VSelect>
                  <div
                    v-if="(errors.length > 0)"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              cols="12"
            >
              <BFormGroup>
                <BFormCheckbox
                  id="is_active"
                  v-model="formProduct.active"
                  switch
                  value="1"
                  name="is_active"
                  unchecked-value="0"
                  :disabled="state.busy"
                >
                  Is Active?
                </BFormCheckbox>
              </BFormGroup>
            </BCol>
          </BRow>

        </form>
      </ValidationObserver>

      <template #modal-footer="{ cancel }">
        <BButton
          variant="primary"
          :disabled="state.busy"
          @click.prevent="validateProduct"
        >
          Update Product
        </BButton>
        <BButton

          variant="outline-dark"
          :disabled="state.busy"
          @click.prevent="discardBtnEvent => preventAriaHidden(discardBtnEvent, cancel)"
        >
          Discard
        </BButton>
      </template>

    </BModal>
  </BContainer>
</template>

<script>
import QUERY from 'lodash'
import { mapGetters } from 'vuex'

import MxMisc from '@/mixins/misc'
import MxFormatter from '@/mixins/formatter'
import MProductCategories from '@/mixins/maintenances/product-categories'

import { SAdminProduct } from '@/services/admin'

export default {
  name: 'AdminProducts',

  mixins: [
    MxMisc,
    MxFormatter,
    MProductCategories
  ],

  middleware: [
    'auth',
    'admin'
  ],

  metaInfo: () => ({
    title: 'Products'
  }),

  data () {
    return {
      state: {
        busy: false,
        synchronizing: false,
        importing: false
      },
      synchronize: {
        start_date: '',
        end_date: ''
      },
      formProduct: {
        id: '',
        category_id: '',
        product_code: '',
        product_name: '',
        reference: '',
        valid: '',
        active: 1
      },
      tableProducts: {
        busy: false,
        fetching: {
          productCategories: false
        },
        options: {
          productCategories: []
        },
        selected: {
          productCategory: ''
        },
        filter: {
          search: '',
          category: ''
        },
        filterOn: [],
        stacked: 'sm',
        sortBy: 'id',
        sortDesc: false,
        sortDirection: 'asc',
        pageOptions: [10, 25, 50, 100],
        currentPage: 1,
        totalRows: 0,
        perPage: 10,
        fields: [
          { mobile: 0, key: 'index', label: '#', class: 'text-center' },
          { mobile: 7, key: 'action', class: 'text-center' },
          { mobile: 1, key: 'created_at', class: 'min-w-150px', formatter: this.dateTimeShortFormatter, sortable: true },
          { mobile: 2, key: 'product_code', class: 'min-w-175px' },
          { mobile: 3, key: 'product_name', class: 'min-w-175px' },
          { mobile: 3, key: 'category.category_name', label: 'Category', class: 'min-w-175px' },
          { mobile: 4, key: 'active', class: 'text-center', formatter: this.yesOrNo },
          { mobile: 5, key: 'valid', class: 'text-center' },
          { mobile: 6, key: 'updated_at', class: 'min-w-150px', formatter: this.dateTimeShortFormatter, sortable: true }
        ].sort(this.scaleOnMobile)
      }
    }
  },

  computed: mapGetters({
    currentUser: 'auth/user'
  }),

  watch: {
    'selected.productCategory' (category) {
      this.formProduct.category_id = category?.id || null
    },

    'tableProducts.selected.productCategory' (category) {
      this.tableProducts.filter.category = category?.id || ''
    }
  },

  mounted () {
    if (this.currentUser) {
      this.getProductCategories(this.tableProducts)
    }
  },

  methods: {

    async tableProvider ({ currentPage, perPage, sortBy, sortDesc, filter }) {
      this.tableProducts.busy = true

      const payload = {
        page: currentPage,
        per_page: perPage,
        sort: sortBy,
        sort_desc: sortDesc,
        filter_text: filter.search,
        category_id: filter.category
      }

      return await SAdminProduct.get(payload).then(
        ({ data }) => {
          this.tableProducts.totalRows = data.total_rows
          return data.items
        }
      ).catch(() => {
        this.tableProducts.totalRows = 0
        return []
      }).finally(() => {
        this.tableProducts.busy = false
      })
    },

    async searchFilterProductCategories (search, loading) {
      this.searchProductCategoriesProvider(this.tableProducts, search, loading)
    },

    async synchronizeProducts () {
      await this.$refs.formSynchronizer.validate().then(
        async validSynchronizer => {
          if (!validSynchronizer) {
            return this.swalInvalid()
          }

          this.state.synchronizing = true

          return SAdminProduct.sync(this.synchronize).then(
            ({ data: { message } }) => {
              this.swalSuccess(message).then(() => {
                this.synchronize.start_date = null
                this.synchronize.end_date = null
                this.$refs.formSynchronizer.reset()
                this.$refs.tableProducts.refetch()
              })
            }
          ).finally(() => {
            this.state.synchronizing = false
          })
        }
      )
    },

    async importProducts () {
      this.state.importing = true
      return SAdminProduct.import().then(
        ({ data: { message } }) => {
          this.swalSuccess(message).then(() => {
            this.$refs.tableProducts.refetch()
          })
        }
      ).finally(() => {
        this.state.importing = false
      })
    },

    editProduct (product) {
      this.formProduct = Object.assign(this.formProduct, {
        id: product.id,
        category_id: product.category_id,
        product_code: product.product_code,
        product_name: product.product_name,
        reference: product.reference,
        valid: product.valid,
        active: product.active
      })

      this.getProductCategories(this).finally(() => {
        this.selected.productCategory = QUERY.find(this.options.productCategories, {
          id: Number(product.category_id)
        })
      })

      this.$refs.modalProduct.show()
    },

    async validateProduct () {
      await this.$refs.formProduct.validate().then(
        async validProduct => {
          if (!validProduct) {
            return this.swalInvalid()
          }

          this.swalConfirm({
            html: '<h6>Update this Product?</h6>',
            confirmButtonText: 'Update',
            preConfirm: () => {
              this.state.busy = true

              return this.putProduct().catch(
                ({ message }) => {
                  this.$refs.formProduct.setErrors(message.errors)
                }
              ).finally(() => {
                this.state.busy = false
              })
            }
          })
        }
      )
    },

    async putProduct () {
      return await SAdminProduct.put(this.formProduct).then(
        ({ data: { message, product } }) => {
          this.swalSuccess(message).then(() => {
            this.$refs.modalProduct.hide()
            this.$refs.tableProducts.updateRow({
              id: product.id,
              category: product.category,
              category_id: product.category_id,
              product_code: product.product_code,
              product_name: product.product_name,
              active: product.active,
              reference: product.reference,
              valid: product.valid,
              updated_at: product.updated_at
            })
          })
        }
      )
    }
  }
}
</script>
